import axios from 'axios'
import { clearAccessToken, sessionKeptThroughBusyMinute } from '../shared/api/tokenStore'
import { getAccessToken } from '../shared/api/tokenStore'
import { APP_STORAGE_PREFIX } from '../config/config.js';
// Token freshness + /auth/refresh single-flight are shared app-wide (see
// shared/api/tokenRefresh.js) so this identity client and the shared API
// client never fire independent, competing refreshes with the same rotating
// refresh token.
import { ensureFreshToken } from '../shared/api/tokenRefresh'
import { markApiActivity } from '../shared/api/sessionPolicy'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/identity'
const IDENTITY_URL = import.meta.env.VITE_IDENTITY_API_URL || '/api/identity'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
})

// Request interceptor to add JWT token (with proactive refresh)
apiClient.interceptors.request.use(
  async (config) => {
    const token = await ensureFreshToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // A request that CHANGES something is somebody pressing something, and until now nothing
    // an API did counted as being here at all — a twenty-minute upload or a long report left a
    // person looking idle. Reads are deliberately excluded: this app runs background pollers,
    // and if a GET counted, a dashboard left open would hold a session open forever.
    markApiActivity(config)
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 that only echoes a busy minute (no token because the refresh met a 429 / restart /
    // dropped connection, and a signed-in person is stored) is not the session ending. This client -
    // Profile, Change my password, the super-admin screens - was still signing people out on it after
    // the shared client stopped (review of 21 Sep). Same rule, same function, both clients.
    if (error.response?.status === 401 && sessionKeptThroughBusyMinute()) {
      console.warn('401 on a request sent while the sign-in service was busy - keeping the session')
      error.userMessage = 'The server is busy. Please try again in a moment.'
      error.sessionKept = true
      return Promise.reject(error)
    }
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.warn('Received 401 Unauthorized response - clearing authentication state')
      
      // Clear stored tokens
      clearAccessToken(); localStorage.removeItem(`${APP_STORAGE_PREFIX}_access_token`)
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_refresh_token`)
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_user`)
      
      // Only redirect if we're not already on the login page to avoid redirect loops
      const currentPath = window.location.pathname
      if (currentPath !== '/login' && !currentPath.includes('/reset-password')) {
        console.log('Redirecting to login due to 401 error')
        
        // Use a small delay to allow the current request to complete
        // We're using window.location directly to reset the app state completely
        setTimeout(() => {
          window.location.href = '/login'
        }, 100)
      }
    }
    return Promise.reject(error)
  }
)

// Authentication API functions
export const authAPI = {
  // Login user
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', {
      email: credentials.email,
      password: credentials.password
    })
    return response.data
  },

  // Register new user (admin only)
  register: async (userData) => {
    const response = await apiClient.post('/user/register', userData)
    return response.data
  },

  // Validate token
  validateToken: async () => {
    const response = await apiClient.get('/auth/verify-token')
    return response.data
  },
  
  // Refresh access token. Phase B1: the refresh token is an httpOnly cookie (sent automatically on this
  // same-origin request); a legacy localStorage copy is sent in the body only for pre-migration sessions.
  // X-Requested-With is future-ready defense-in-depth (CSRF is currently provided by SameSite=Strict).
  refreshToken: async () => {
    const legacyRefresh = localStorage.getItem(`${APP_STORAGE_PREFIX}_refresh_token`)
    const response = await axios.post(
      `${IDENTITY_URL}/auth/refresh`,
      legacyRefresh ? { refresh_token: legacyRefresh } : null,
      { withCredentials: true, headers: { 'X-Requested-With': 'XMLHttpRequest' } },
    )
    return response.data
  },
  
  // Logout and revoke token
  logout: async () => {
    const response = await apiClient.post('/auth/logout')
    return response.data
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    const response = await apiClient.post('/auth/request-reset', { email })
    return response.data
  },

  // Reset password with token
  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      new_password: newPassword
    })
    return response.data
  },

  // Change password (authenticated user)
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    })
    return response.data
  },

  // Get current user profile
  getProfile: async () => {
    const response = await apiClient.get('/user/me')
    return response.data
  },

  // Update user profile
  updateProfile: async (profileData) => {
    const response = await apiClient.put('/user/me', profileData)
    return response.data
  },

  // Get all users (admin only)
  getUsers: async () => {
    const response = await apiClient.get('/user/')
    return response.data
  },

  // Delete user (admin only)
  deleteUser: async (userId) => {
    const response = await apiClient.delete(`/user/${userId}`)
    return response.data
  },
  
  // Update user role (admin only)
  updateUserRole: async (userId, role) => {
    const response = await apiClient.put(`/user/${userId}/role`, { role })
    return response.data
  },
  
  // Update user module access (admin only)
  updateUserModules: async (userId, accessibleModules) => {
    const response = await apiClient.put(`/user/${userId}/modules`, { 
      accessible_modules: accessibleModules 
    })
    return response.data
  }
}

// Health check API
export const healthAPI = {
  check: async () => {
    const response = await apiClient.get('/health')
    return response.data
  }
}

// Export the configured axios instance for custom requests
export default apiClient
